import type { GeoJSONFeature } from '../types/gis';

/** Compute the [[minLng, minLat], [maxLng, maxLat]] bbox over an arbitrary
 *  list of features. Returns null when no positions can be extracted (empty
 *  list, all features unlocated, or only unsupported geometry types). */
export function computeFeaturesBbox(
  features: GeoJSONFeature[],
): [[number, number], [number, number]] | null {
  let minLng = Infinity, minLat = Infinity;
  let maxLng = -Infinity, maxLat = -Infinity;
  let count = 0;

  const visit = (lng: number, lat: number) => {
    if (lng < minLng) minLng = lng;
    if (lat < minLat) minLat = lat;
    if (lng > maxLng) maxLng = lng;
    if (lat > maxLat) maxLat = lat;
    count++;
  };

  for (const feat of features) {
    const geom: any = feat?.geometry;
    if (!geom?.coordinates) continue;
    if (geom.type === 'Point') {
      const [lng, lat] = geom.coordinates as [number, number];
      visit(lng, lat);
    } else if (geom.type === 'LineString' || geom.type === 'MultiPoint') {
      for (const [lng, lat] of geom.coordinates as Array<[number, number]>) {
        visit(lng, lat);
      }
    } else if (geom.type === 'Polygon' || geom.type === 'MultiLineString') {
      for (const ring of geom.coordinates as Array<Array<[number, number]>>) {
        for (const [lng, lat] of ring) visit(lng, lat);
      }
    } else if (geom.type === 'MultiPolygon') {
      for (const poly of geom.coordinates as Array<Array<Array<[number, number]>>>) {
        for (const ring of poly) for (const [lng, lat] of ring) visit(lng, lat);
      }
    }
  }

  if (count === 0) return null;
  return [[minLng, minLat], [maxLng, maxLat]];
}
